import React from 'react';
import PropTypes from 'prop-types';

import {
  CartesianGrid,
  LineChart,
  YAxis,
  Line,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import moment from 'moment';

import '../common.scss';
import './Charts.css';

const ChartDateTick = ({
  x, y, payload, showValue,
}) => {
  const date = moment(payload.value);
  // TODO: adjust precision of tick timestamp based on selected range
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} fill="#666">
        {showValue ? (
          <tspan textAnchor="middle" x="0" fontSize="14px">
            {date.format('MMM D, YYYY')}
          </tspan>
        ) : (
          ''
        )}
        {showValue ? (
          <tspan textAnchor="middle" x="0" dy="20" fontSize="12px">
            {date.format('H:mm:ss.SS')}
          </tspan>
        ) : (
          ''
        )}
      </text>
    </g>
  );
};

ChartDateTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({ value: PropTypes.number }),
  showValue: PropTypes.bool.isRequired,
};

ChartDateTick.defaultProps = {
  x: 0,
  y: 0,
  payload: {},
};

function createSeries(dataSet = [], pivotDate = moment()) {
  return [
    {
      name: 'Measured',
      data: dataSet.filter((ds) => ds.timestamp <= pivotDate.valueOf()),
    },
    {
      name: 'Predicted',
      data: dataSet.filter((ds) => ds.timestamp > pivotDate.valueOf()),
    },
  ];
}

export default function CsrmCharts({
  types,
  chartMargins,
  dataSets,
  maxMeasuredDate,
  timeRange,
}) {
  if (!types.length) {
    return (
      <span className="no-charts-warning">
        Add charts above to see trend lines
      </span>
    );
  }

  const charts = [];
  types.forEach((dataKey, idx) => {
    const dataSet = dataSets[dataKey];
    if (!dataSet) return;

    const lines = createSeries(dataSet.data, maxMeasuredDate).map((series) => (
      <Line
        key={`line-${dataKey}-${series.name}`}
        data={series.data}
        type="monotone"
        dataKey="value"
        stroke={dataSet.color}
        strokeWidth={2}
        strokeDasharray={series.name === 'Measured' ? '' : '3 3'}
        dot={false}
      />
    ));

    // TODO: set specific number of ticks or interval
    const xAxisProps = {
      allowDuplicatedCategory: false,
      type: 'number',
      domain: timeRange.map((tr) => tr.valueOf()),
      tick: <ChartDateTick showValue={idx === types.length - 1} />,
    };

    charts.push(
      <div key={`chart-container-${dataKey}`} className="chart-container">
        <h3>{dataSet.name}</h3>
        <span className="chart-y-axis-label">{dataSet.unit}</span>
        <ResponsiveContainer height={250}>
          <LineChart margin={chartMargins}>
            <Tooltip
              labelFormatter={(text) => moment(text).format('MMM D, YYYY HH:mm:ss.SSS')}
            />
            <XAxis dataKey="timestamp" {...xAxisProps} />
            <YAxis dataKey="value" />
            <CartesianGrid strokeDasharray="3 3" />
            {lines}
          </LineChart>
        </ResponsiveContainer>
      </div>,
    );
  });

  return charts;
}

CsrmCharts.propTypes = {
  types: PropTypes.arrayOf(PropTypes.string).isRequired,
  chartMargins: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
  }).isRequired,
  dataSets: PropTypes.object.isRequired,
  maxMeasuredDate: PropTypes.object.isRequired,
  timeRange: PropTypes.arrayOf(PropTypes.number).isRequired,
};
